import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { X, Users, Scale, FileText, Paperclip, Gavel, Clock, CheckCircle, XCircle, ChevronDown, ChevronUp, Trash2 } from 'lucide-react';

// =====================================================================
// PLENARIUM:COMISSOES -- dentro da materia no Tramite (admin):
// encaminhar as comissoes, designar relator e prazo, acompanhar a
// votacao do parecer, registrar parecer entregue na secretaria, anexar
// o documento assinado e lancar o parecer juridico.
// =====================================================================

export const CONCLUSAO = { FAVORAVEL: 'Favorável', FAVORAVEL_EMENDAS: 'Favorável com emendas', CONTRARIO: 'Contrário', FAVORAVEL_RESSALVAS: 'Favorável com ressalvas' };
export const COR_CONCLUSAO = { FAVORAVEL: 'badge-verde', FAVORAVEL_EMENDAS: 'badge-verde', FAVORAVEL_RESSALVAS: 'badge-verde', CONTRARIO: 'badge-vermelho' };
export const STATUS_REL = {
  AGUARDANDO_RELATOR: ['Aguardando relator', 'badge-cinza'],
  EM_ELABORACAO: ['Relator elaborando', 'badge-amarelo'],
  EM_VOTACAO: ['Em votação na comissão', 'badge-azul'],
  CONCLUIDO: ['Concluído', 'badge-verde']
};
const VOTO = { ACOMPANHA: 'Acompanha', DIVERGE: 'Diverge', ABSTENCAO: 'Abstenção' };
const dataBR = (d) => (d ? new Date(d).toLocaleDateString('pt-BR') : '');
const hojeISO = () => new Date().toISOString().slice(0, 10);
const LIMITE_MB = 5;

function lerArquivo(e, cb) {
  const f = e.target.files[0]; if (!f) return;
  if (f.size > LIMITE_MB * 1024 * 1024) { toast.error(`O limite é ${LIMITE_MB}MB.`); e.target.value = ''; return; }
  const r = new FileReader(); r.onload = () => cb({ arquivo: r.result, nome: f.name.replace(/\.[^.]+$/, '') }); r.readAsDataURL(f);
}
async function abrirAnexo(id) {
  try {
    const r = await axios.get(`/api/projetos/anexos/${id}`);
    const url = r.data.arquivo; if (!url) return;
    if (!url.startsWith('data:')) { window.open(url, '_blank'); return; }
    const [cab, corpo] = url.split(','); const tipo = (cab.match(/:(.*?);/) || [])[1] || 'application/pdf';
    const bin = atob(corpo); const u8 = new Uint8Array(bin.length); for (let i = 0; i < bin.length; i++) u8[i] = bin.charCodeAt(i);
    window.open(URL.createObjectURL(new Blob([u8], { type: tipo })), '_blank');
  } catch { toast.error('Erro ao abrir o documento'); }
}

export default function PareceresMateria({ projeto, onMudou }) {
  const [dados, setDados] = useState(null);
  const [comissoes, setComissoes] = useState([]);
  const [encaminhar, setEncaminhar] = useState(null);
  const [registrar, setRegistrar] = useState(null);
  const [juridico, setJuridico] = useState(null);
  const [aberto, setAberto] = useState({});
  const [ocupado, setOcupado] = useState(false);

  const carregar = useCallback(() => {
    axios.get(`/api/comissoes/projeto/${projeto.id}`).then(r => setDados(r.data)).catch(() => setDados({ relatorias: [], juridicos: [], erro: true }));
  }, [projeto.id]);
  useEffect(() => { carregar(); axios.get('/api/comissoes').then(r => setComissoes((r.data || []).filter(c => c.ativa))).catch(() => {}); }, [carregar]);
  const mudou = () => { carregar(); if (onMudou) onMudou(); };

  const acao = async (fn, ok) => {
    setOcupado(true);
    try { await fn(); if (ok) toast.success(ok); mudou(); return true; }
    catch (err) { toast.error(err.response?.data?.error || 'Não foi possível concluir'); return false; }
    finally { setOcupado(false); }
  };
  const membrosDe = (comissaoId) => ((comissoes.find(c => c.id === comissaoId) || {}).membros || []);

  if (!dados) return <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)' }}>Carregando comissões e pareceres...</p>;
  const rotulo = { fontSize: '0.68rem', fontWeight: 700, color: 'var(--texto-suave)', textTransform: 'uppercase', letterSpacing: 1, margin: 0 };

  return (
    <div style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, marginBottom: 8, flexWrap: 'wrap' }}>
        <p style={rotulo}>Comissões e Pareceres</p>
        <div style={{ display: 'flex', gap: 6 }}>
          <button className="btn btn-secondary btn-sm" onClick={() => setEncaminhar({ comissaoId: '', relatorId: '', prazo: '' })}><Users size={13} /> Encaminhar à comissão</button>
          <button className="btn btn-secondary btn-sm" onClick={() => setJuridico({ conclusao: 'FAVORAVEL', orgao: 'Procuradoria Jurídica', parecerista: '', oab: '', data: hojeISO(), resumo: '', anexo: null })}><Scale size={13} /> Parecer jurídico</button>
        </div>
      </div>

      {dados.relatorias.length === 0 && dados.juridicos.length === 0 && (
        <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', fontStyle: 'italic' }}>A matéria ainda não foi encaminhada a nenhuma comissão.</p>
      )}

      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {dados.relatorias.map(r => {
          const [st, cor] = STATUS_REL[r.status] || [r.status, 'badge-cinza'];
          const ab = !!aberto[r.id];
          return (
            <div key={r.id} style={{ background: 'var(--cinza-claro)', border: `1px solid ${r.prazoVencido ? 'var(--reprovado)' : 'var(--cinza)'}`, borderRadius: 8, padding: '10px 12px' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 220 }}>
                  <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                    <strong style={{ fontSize: '0.82rem' }}>{r.comissao.sigla ? `${r.comissao.sigla} — ` : ''}{r.comissao.nome}</strong>
                    <span className={`badge ${cor}`}>{st}</span>
                    {r.resultado && <span className={`badge ${r.resultado === 'APROVADO' ? 'badge-verde' : 'badge-vermelho'}`}>Parecer {r.resultado === 'APROVADO' ? 'aprovado' : 'rejeitado'}{r.placar ? ` · ${r.placar}` : ''}</span>}
                    {r.parecerConclusao && <span className={`badge ${COR_CONCLUSAO[r.parecerConclusao]}`}>{CONCLUSAO[r.parecerConclusao]}</span>}
                    {r.origem === 'SECRETARIA' && <span className="badge badge-cinza">Entregue na secretaria</span>}
                  </div>
                  <div style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 4, display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                    <span>Relator(a):{' '}
                      {r.status === 'CONCLUIDO' || r.status === 'EM_VOTACAO' ? <b>{r.relator ? r.relator.nome : '—'}</b> : (
                        <select className="form-input form-select" style={{ display: 'inline-block', width: 'auto', padding: '2px 26px 2px 6px', fontSize: '0.75rem', height: 'auto' }} value={r.relator ? r.relator.id : ''} disabled={ocupado}
                          onChange={e => acao(() => axios.patch(`/api/comissoes/relatoria/${r.id}`, { relatorId: e.target.value || null }), 'Relator designado!')}>
                          <option value="">— designar —</option>
                          {r.membros.map(m => <option key={m.vereadorId} value={m.vereadorId}>{m.nome}</option>)}
                        </select>
                      )}
                    </span>
                    <span style={{ color: r.prazoVencido ? 'var(--reprovado)' : undefined, fontWeight: r.prazoVencido ? 700 : 400 }}><Clock size={11} style={{ verticalAlign: '-1px' }} /> Prazo:{' '}
                      {r.status === 'CONCLUIDO' ? (dataBR(r.prazo) || '—') : (
                        <input type="date" className="form-input" style={{ display: 'inline-block', width: 'auto', padding: '2px 6px', fontSize: '0.75rem', height: 'auto' }} value={r.prazo ? String(r.prazo).slice(0, 10) : ''} disabled={ocupado}
                          onChange={e => acao(() => axios.patch(`/api/comissoes/relatoria/${r.id}`, { prazo: e.target.value || null }), 'Prazo salvo!')} />
                      )}
                      {r.prazoVencido && ' · VENCIDO'}
                    </span>
                    {r.resultadoEm && <span>Concluído em {dataBR(r.resultadoEm)}</span>}
                  </div>
                </div>
                <div style={{ display: 'flex', gap: 5, flexWrap: 'wrap' }}>
                  {r.anexoId && <button className="btn btn-secondary btn-sm" onClick={() => abrirAnexo(r.anexoId)}><FileText size={13} /> PDF assinado</button>}
                  <label className="btn btn-secondary btn-sm" style={{ cursor: 'pointer', margin: 0 }} title="Anexar o parecer assinado (PDF)">
                    <Paperclip size={13} /> {r.anexoId ? 'Trocar PDF' : 'Anexar assinado'}
                    <input type="file" accept=".pdf,image/*" style={{ display: 'none' }} onChange={e => lerArquivo(e, (a) => acao(() => axios.post(`/api/comissoes/relatoria/${r.id}/anexo`, { nome: `Parecer ${r.comissao.sigla || r.comissao.nome}`, arquivo: a.arquivo }), 'Documento anexado!'))} />
                  </label>
                  {r.status === 'EM_VOTACAO' && <button className="btn btn-primary btn-sm" disabled={ocupado} onClick={() => { if (window.confirm(`Encerrar a votação do parecer na ${r.comissao.sigla || r.comissao.nome}?\n\nAcompanham: ${r.contagem.ACOMPANHA} · Divergem: ${r.contagem.DIVERGE} · Abstenções: ${r.contagem.ABSTENCAO}`)) acao(() => axios.post(`/api/comissoes/relatoria/${r.id}/encerrar`), 'Votação encerrada!'); }}><Gavel size={13} /> Encerrar votação</button>}
                  {r.status !== 'CONCLUIDO' && <button className="btn btn-secondary btn-sm" onClick={() => setRegistrar({ r, conclusao: r.parecerConclusao || 'FAVORAVEL', resultado: 'APROVADO', placar: '', relatorId: r.relator ? r.relator.id : '', data: hojeISO(), texto: r.parecerTexto || '' })}>Registrar parecer</button>}
                  {(r.status === 'AGUARDANDO_RELATOR' || r.status === 'EM_ELABORACAO') && <button className="btn btn-secondary btn-sm" style={{ color: 'var(--reprovado)' }} title="Desfazer o encaminhamento" onClick={() => { if (window.confirm('Desfazer o encaminhamento a esta comissão?')) acao(() => axios.delete(`/api/comissoes/relatoria/${r.id}`), 'Encaminhamento desfeito'); }}><Trash2 size={13} /></button>}
                  {(r.parecerTexto || r.status === 'EM_VOTACAO' || r.status === 'CONCLUIDO') && <button className="btn btn-secondary btn-sm" onClick={() => setAberto(a => ({ ...a, [r.id]: !ab }))}>{ab ? <ChevronUp size={13} /> : <ChevronDown size={13} />}</button>}
                </div>
              </div>
              {ab && (
                <div style={{ marginTop: 10, borderTop: '1px solid var(--cinza)', paddingTop: 8 }}>
                  {r.parecerTexto ? <p style={{ fontSize: '0.8rem', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}>{r.parecerTexto}</p> : <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', fontStyle: 'italic' }}>Sem texto do parecer.</p>}
                  {(r.status === 'EM_VOTACAO' || (r.status === 'CONCLUIDO' && r.origem !== 'SECRETARIA')) && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 8 }}>
                      {r.membros.filter(m => m.funcao !== 'SUPLENTE').map(m => (
                        <span key={m.vereadorId} className={`badge ${m.voto === 'ACOMPANHA' ? 'badge-verde' : m.voto === 'DIVERGE' ? 'badge-vermelho' : m.voto ? 'badge-amarelo' : 'badge-cinza'}`}>
                          {m.voto === 'ACOMPANHA' ? <CheckCircle size={9} /> : m.voto === 'DIVERGE' ? <XCircle size={9} /> : null} {m.nome}: {m.voto ? VOTO[m.voto] : 'aguardando'}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {dados.juridicos.map(j => (
          <div key={j.id} style={{ background: 'var(--cinza-claro)', border: '1px solid var(--cinza)', borderRadius: 8, padding: '10px 12px', display: 'flex', alignItems: 'flex-start', gap: 10, flexWrap: 'wrap' }}>
            <Scale size={16} style={{ marginTop: 2, color: 'var(--texto-suave)' }} />
            <div style={{ flex: 1, minWidth: 220 }}>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                <strong style={{ fontSize: '0.82rem' }}>Parecer jurídico — {j.orgao || 'Procuradoria Jurídica'}</strong>
                <span className={`badge ${COR_CONCLUSAO[j.conclusao]}`}>{CONCLUSAO[j.conclusao]}</span>
              </div>
              <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginTop: 3 }}>{j.parecerista}{j.oab ? ` · OAB ${j.oab}` : ''}{j.data ? ` · ${dataBR(j.data)}` : ''}</p>
              {j.resumo && <p style={{ fontSize: '0.78rem', marginTop: 4, whiteSpace: 'pre-wrap' }}>{j.resumo}</p>}
            </div>
            <div style={{ display: 'flex', gap: 5 }}>
              {j.anexoId && <button className="btn btn-secondary btn-sm" onClick={() => abrirAnexo(j.anexoId)}><FileText size={13} /> PDF</button>}
              <button className="btn btn-secondary btn-sm" style={{ color: 'var(--reprovado)' }} onClick={() => { if (window.confirm('Remover este parecer jurídico?')) acao(() => axios.delete(`/api/comissoes/juridico/${j.id}`), 'Removido'); }}><Trash2 size={13} /></button>
            </div>
          </div>
        ))}
      </div>

      {encaminhar && (
        <div className="modal-overlay" onClick={() => setEncaminhar(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 500 }}>
            <div className="modal-header">
              <h2 className="modal-title"><Users size={16} /> Encaminhar à comissão — {projeto.numero}</h2>
              <button className="btn-modal-close" onClick={() => setEncaminhar(null)}><X size={18} /></button>
            </div>
            {comissoes.length === 0 ? <p style={{ fontSize: '0.85rem' }}>Cadastre as comissões no menu <b>Comissões</b> primeiro.</p> : (
              <form onSubmit={async (e) => { e.preventDefault(); if (await acao(() => axios.post(`/api/comissoes/projeto/${projeto.id}/encaminhar`, encaminhar), 'Matéria encaminhada!')) setEncaminhar(null); }}>
                <div className="form-group">
                  <label className="form-label">Comissão *</label>
                  <select className="form-input form-select" value={encaminhar.comissaoId} onChange={e => setEncaminhar({ ...encaminhar, comissaoId: e.target.value, relatorId: '' })} required>
                    <option value="">Escolha a comissão</option>
                    {comissoes.map(c => <option key={c.id} value={c.id}>{c.sigla ? `${c.sigla} — ` : ''}{c.nome}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Relator(a)</label>
                  <select className="form-input form-select" value={encaminhar.relatorId} onChange={e => setEncaminhar({ ...encaminhar, relatorId: e.target.value })} disabled={!encaminhar.comissaoId}>
                    <option value="">Designar depois (o presidente da comissão pode designar pelo tablet)</option>
                    {membrosDe(encaminhar.comissaoId).map(m => <option key={m.vereadorId} value={m.vereadorId}>{m.nome}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Prazo para o parecer</label>
                  <input type="date" className="form-input" value={encaminhar.prazo} onChange={e => setEncaminhar({ ...encaminhar, prazo: e.target.value })} />
                </div>
                <div className="modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={() => setEncaminhar(null)}>Cancelar</button>
                  <button type="submit" className="btn btn-primary" disabled={ocupado}>Encaminhar</button>
                </div>
              </form>
            )}
          </div>
        </div>
      )}

      {registrar && (
        <div className="modal-overlay" onClick={() => setRegistrar(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title">Registrar parecer — {registrar.r.comissao.sigla || registrar.r.comissao.nome}</h2>
              <button className="btn-modal-close" onClick={() => setRegistrar(null)}><X size={18} /></button>
            </div>
            <p style={{ fontSize: '0.75rem', color: 'var(--texto-suave)', marginBottom: 12 }}>Use quando o parecer foi entregue em papel na secretaria. Depois anexe o documento assinado.</p>
            <form onSubmit={async (e) => { e.preventDefault(); const { r, ...corpo } = registrar; if (await acao(() => axios.post(`/api/comissoes/relatoria/${r.id}/registrar`, corpo), 'Parecer registrado!')) setRegistrar(null); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Relator(a)</label>
                  <select className="form-input form-select" value={registrar.relatorId} onChange={e => setRegistrar({ ...registrar, relatorId: e.target.value })}>
                    <option value="">—</option>
                    {registrar.r.membros.map(m => <option key={m.vereadorId} value={m.vereadorId}>{m.nome}</option>)}
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Conclusão do relator *</label>
                  <select className="form-input form-select" value={registrar.conclusao} onChange={e => setRegistrar({ ...registrar, conclusao: e.target.value })}>
                    <option value="FAVORAVEL">Favorável</option><option value="FAVORAVEL_EMENDAS">Favorável com emendas</option><option value="CONTRARIO">Contrário</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Resultado na comissão *</label>
                  <select className="form-input form-select" value={registrar.resultado} onChange={e => setRegistrar({ ...registrar, resultado: e.target.value })}>
                    <option value="APROVADO">Parecer aprovado</option><option value="REJEITADO">Parecer rejeitado</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Placar</label>
                  <input className="form-input" value={registrar.placar} onChange={e => setRegistrar({ ...registrar, placar: e.target.value })} placeholder="Ex.: 3 a 0" />
                </div>
                <div className="form-group">
                  <label className="form-label">Data</label>
                  <input type="date" className="form-input" value={registrar.data} onChange={e => setRegistrar({ ...registrar, data: e.target.value })} />
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Texto do parecer (opcional)</label>
                  <textarea className="form-input" rows={6} value={registrar.texto} onChange={e => setRegistrar({ ...registrar, texto: e.target.value })} placeholder="Voto do relator, fundamentação e conclusão" />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setRegistrar(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={ocupado}>Registrar</button>
              </div>
            </form>
          </div>
        </div>
      )}

      {juridico && (
        <div className="modal-overlay" onClick={() => setJuridico(null)}>
          <div className="modal" onClick={e => e.stopPropagation()} style={{ maxWidth: 600, maxHeight: 'calc(100vh - 32px)', overflowY: 'auto' }}>
            <div className="modal-header">
              <h2 className="modal-title"><Scale size={16} /> Parecer jurídico — {projeto.numero}</h2>
              <button className="btn-modal-close" onClick={() => setJuridico(null)}><X size={18} /></button>
            </div>
            <form onSubmit={async (e) => { e.preventDefault(); if (await acao(() => axios.post(`/api/comissoes/projeto/${projeto.id}/juridico`, juridico), 'Parecer jurídico lançado!')) setJuridico(null); }}>
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
                <div className="form-group">
                  <label className="form-label">Conclusão *</label>
                  <select className="form-input form-select" value={juridico.conclusao} onChange={e => setJuridico({ ...juridico, conclusao: e.target.value })}>
                    <option value="FAVORAVEL">Favorável</option><option value="FAVORAVEL_RESSALVAS">Favorável com ressalvas</option><option value="CONTRARIO">Contrário</option>
                  </select>
                </div>
                <div className="form-group">
                  <label className="form-label">Órgão</label>
                  <input className="form-input" value={juridico.orgao} onChange={e => setJuridico({ ...juridico, orgao: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Parecerista *</label>
                  <input className="form-input" value={juridico.parecerista} onChange={e => setJuridico({ ...juridico, parecerista: e.target.value })} placeholder="Nome do procurador(a)" required />
                </div>
                <div className="form-group">
                  <label className="form-label">OAB</label>
                  <input className="form-input" value={juridico.oab} onChange={e => setJuridico({ ...juridico, oab: e.target.value })} placeholder="AM 12.345" />
                </div>
                <div className="form-group">
                  <label className="form-label">Data</label>
                  <input type="date" className="form-input" value={juridico.data} onChange={e => setJuridico({ ...juridico, data: e.target.value })} />
                </div>
                <div className="form-group">
                  <label className="form-label">Documento assinado (PDF)</label>
                  <input type="file" accept=".pdf,image/*" className="form-input" onChange={e => lerArquivo(e, (a) => setJuridico(j => ({ ...j, anexo: { nome: a.nome || 'Parecer jurídico', arquivo: a.arquivo } })))} />
                  {juridico.anexo && <p style={{ fontSize: '0.72rem', color: 'var(--aprovado)', marginTop: 3 }}>✅ Arquivo pronto</p>}
                </div>
                <div className="form-group" style={{ gridColumn: '1/-1' }}>
                  <label className="form-label">Resumo</label>
                  <textarea className="form-input" rows={5} value={juridico.resumo} onChange={e => setJuridico({ ...juridico, resumo: e.target.value })} placeholder="Conclusão e fundamentos principais (constitucionalidade, legalidade, técnica legislativa)" />
                </div>
              </div>
              <div className="modal-actions">
                <button type="button" className="btn btn-secondary" onClick={() => setJuridico(null)}>Cancelar</button>
                <button type="submit" className="btn btn-primary" disabled={ocupado}>Lançar parecer</button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
